import { ClearRounded, DeleteOutlineRounded, FileDownloadRounded, RefreshRounded, UploadRounded } from "@mui/icons-material";
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	Typography,
} from "@mui/material";
import ExcelJS from "exceljs";
import TanstackDatagrid from "components/ReusableComponents/TanstackDatagrid/TanstackDatagrid";
import { PimDashboardState } from "context/PimDashboardContext";
import { useMemo, useState } from "react";

const ID_KEY_OPTIONS = {
	option_id: { key: "option_id", name: "option_id", headers: ["option_id"] },
	style_ref_no: { key: "style_ref_no", name: "style_ref_no", headers: ["style_ref_no"] },
};

const COLOR_LEVEL_ID_TYPES = {
	option_id: {
		missingDetailKey: "missing_option_ids",
		rejectedDetailKey: "rejected_option_ids",
		blockedDetailKey: "blocked_option_ids",
		notFoundLabel: "option_id",
	},
	style_ref_no: {
		missingDetailKey: "missing_style_ref_nos",
		rejectedDetailKey: "rejected_style_ref_nos",
		blockedDetailKey: "blocked_style_ref_nos",
		notFoundLabel: "style_ref_no",
	},
};

const FIELD_UNSET = "__UNSET__";

const OPTIONAL_FIELDS = [
	{ key: "cogs", headers: ["cogs"], label: "cogs", numeric: true },
	{ key: "cogs_remark", headers: ["cogs_remark", "cogs_remarks"], label: "cogs_remark" },
	{ key: "wash_care", headers: ["wash_care"], label: "wash_care" },
	{ key: "hsn_code", headers: ["hsn_code"], label: "hsn_code" },
	{ key: "fabric_code", headers: ["fabric_code"], label: "fabric_code" },
	{ key: "fw_material_code", headers: ["fw_material_code"], label: "fw_material_code" },
];

const normalizeHeader = (value) =>
	String(value ?? "")
		.trim()
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "_")
		.replace(/^_+|_+$/g, "");

const parseCsvLine = (line) => {
	const values = [];
	let current = "";
	let inQuotes = false;

	for (let index = 0; index < line.length; index += 1) {
		const char = line[index];
		const nextChar = line[index + 1];

		if (char === '"') {
			if (inQuotes && nextChar === '"') {
				current += '"';
				index += 1;
			} else {
				inQuotes = !inQuotes;
			}
			continue;
		}

		if (char === "," && !inQuotes) {
			values.push(current.trim());
			current = "";
			continue;
		}

		current += char;
	}

	values.push(current.trim());
	return values;
};

const formatCellValue = (value) => {
	if (value == null) return "";
	if (value instanceof Date && !Number.isNaN(value.getTime())) {
		const pad = (item) => String(item).padStart(2, "0");
		return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())} ${pad(
			value.getHours(),
		)}:${pad(value.getMinutes())}:${pad(value.getSeconds())}`;
	}
	if (typeof value === "object") {
		if (value?.text != null) return String(value.text).trim();
		if (value?.result != null) return String(value.result).trim();
		if (value?.richText?.length)
			return value.richText
				.map((part) => part.text)
				.join("")
				.trim();
	}
	return String(value).trim();
};

const formatOptionalValue = (value, numeric = false) => {
	const rawValue = formatCellValue(value);
	if (rawValue === "") return FIELD_UNSET;
	if (!numeric) return rawValue;
	const numericValue = Number(rawValue);
	return Number.isFinite(numericValue) ? numericValue : rawValue;
};

const normalizeKeyValue = (value) => String(value ?? "").trim();

const emptyFieldIndexes = () => Object.fromEntries(OPTIONAL_FIELDS.map((field) => [field.key, ""]));

const UploadModal = ({ modal, setModal, callbackFun }) => {
	const { allAttributes } = PimDashboardState();
	const washCareOptions = useMemo(() => {
		const washCareAttr = (allAttributes || []).find((attr) => attr?.key === "wash_care");
		return [...new Set((washCareAttr?.attributeValues || []).map((item) => String(item?.value ?? "").trim()).filter(Boolean))];
	}, [allAttributes]);

	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [fileName, setFileName] = useState("");
	const [headers, setHeaders] = useState([]);
	const [sourceRows, setSourceRows] = useState([]);
	const [idKeyType, setIdKeyType] = useState(ID_KEY_OPTIONS.option_id.key);
	const [idColumnIndex, setIdColumnIndex] = useState("");
	const [fieldIndexes, setFieldIndexes] = useState(emptyFieldIndexes);
	const [previewRows, setPreviewRows] = useState([]);
	const [rowFeedback, setRowFeedback] = useState({});

	const idKeyLabel = ID_KEY_OPTIONS[idKeyType]?.name || "option_id";

	const resetState = () => {
		setLoading(false);
		setError("");
		setFileName("");
		setHeaders([]);
		setSourceRows([]);
		setIdColumnIndex("");
		setFieldIndexes(emptyFieldIndexes());
		setPreviewRows([]);
		setRowFeedback({});
	};

	const handleClose = () => {
		resetState();
		setIdKeyType(ID_KEY_OPTIONS.option_id.key);
		setModal(false);
	};

	const parseCsvFile = async (file) => {
		const text = await file.text();
		const lines = text.split(/\r?\n/).filter((line) => line.trim());
		if (!lines.length) return { headers: [], rows: [] };

		const parsedHeaders = parseCsvLine(lines[0]).map((value, index) => value || `Column ${index + 1}`);
		const parsedRows = lines.slice(1).map((line) => parseCsvLine(line));
		return { headers: parsedHeaders, rows: parsedRows };
	};

	const parseExcelFile = async (file) => {
		const workbook = new ExcelJS.Workbook();
		const buffer = await file.arrayBuffer();
		await workbook.xlsx.load(buffer);
		const worksheet = workbook.worksheets[0];
		if (!worksheet) return { headers: [], rows: [] };

		const parsedHeaders = worksheet
			.getRow(1)
			.values.slice(1)
			.map((value, index) => formatCellValue(value) || `Column ${index + 1}`);

		const parsedRows = [];
		worksheet.eachRow((row, rowNumber) => {
			if (rowNumber === 1) return;
			parsedRows.push(row.values.slice(1));
		});
		return { headers: parsedHeaders, rows: parsedRows };
	};

	const getDefaultIndex = (list, targetKeys) => list.findIndex((header) => targetKeys.includes(normalizeHeader(header)));

	const hasAnyFieldMapped = (indexes = fieldIndexes) => OPTIONAL_FIELDS.some((field) => indexes[field.key] !== "");

	const buildMappedRows = (
		rows = sourceRows,
		idColumn = idColumnIndex,
		indexes = fieldIndexes,
		keyType = idKeyType,
	) => {
		const keyLabel = ID_KEY_OPTIONS[keyType]?.name || "option_id";
		if (idColumn === "") throw new Error(`Select ${keyLabel} column first.`);
		if (!hasAnyFieldMapped(indexes)) {
			throw new Error("Select at least one field column (cogs, wash_care, hsn_code, fabric_code, etc.).");
		}

		const selectedIndexes = [idColumn, ...OPTIONAL_FIELDS.map((field) => indexes[field.key]).filter((v) => v !== "")];
		if (new Set(selectedIndexes).size !== selectedIndexes.length) {
			throw new Error("Each mapped column must be unique.");
		}

		const idIndex = Number(idColumn);
		const groupedRows = rows.reduce((acc, row, index) => {
			const keyValue = formatCellValue(row?.[idIndex]);
			if (!keyValue) return acc;

			const parsed = {};
			OPTIONAL_FIELDS.forEach((field) => {
				const col = indexes[field.key];
				parsed[field.key] =
					col === "" ? FIELD_UNSET : formatOptionalValue(row?.[Number(col)], field.numeric === true);
			});

			if (!acc[keyValue]) {
				acc[keyValue] = {
					__rowId: `sourcing-upload-row-${index}`,
					[keyLabel]: keyValue,
					...parsed,
					__status: "Pending",
					__note: "",
					__count: 0,
					__valueSets: Object.fromEntries(OPTIONAL_FIELDS.map((field) => [field.key, new Set()])),
				};
			}

			acc[keyValue].__count += 1;
			OPTIONAL_FIELDS.forEach((field) => {
				if (parsed[field.key] === FIELD_UNSET) return;
				acc[keyValue].__valueSets[field.key].add(String(parsed[field.key]));
				acc[keyValue][field.key] = parsed[field.key];
			});
			return acc;
		}, {});

		return Object.values(groupedRows).map((row) => {
			const hasAnyField = OPTIONAL_FIELDS.some((field) => row[field.key] !== FIELD_UNSET);
			if (!hasAnyField) {
				return { ...row, __isValid: false, __status: "Invalid", __note: "No updatable fields provided" };
			}
			if (row.cogs !== FIELD_UNSET && Number.isNaN(Number(row.cogs))) {
				return { ...row, __isValid: false, __status: "Invalid", __note: "Missing or invalid cogs" };
			}
			if (row.hsn_code !== FIELD_UNSET && !/^\d{8}$/.test(String(row.hsn_code))) {
				return { ...row, __isValid: false, __status: "Invalid", __note: "HSN Code must be exactly 8 digits" };
			}
			if (OPTIONAL_FIELDS.some((field) => row.__valueSets[field.key].size > 1)) {
				return {
					...row,
					__isValid: false,
					__status: "Duplicate",
					__note: `${keyLabel} has conflicting values in the file`,
				};
			}
			return {
				...row,
				__isValid: true,
				__status: "Valid",
				__note: row.__count > 1 ? `Merged ${row.__count} rows with the same data` : "Ready to upload",
			};
		});
	};

	const detectColumnsForKeyType = (parsedHeaders, keyType) => {
		const keyHeaders = ID_KEY_OPTIONS[keyType]?.headers || ["option_id"];
		const idIndex = getDefaultIndex(parsedHeaders, keyHeaders);
		const nextIndexes = emptyFieldIndexes();
		OPTIONAL_FIELDS.forEach((field) => {
			const found = getDefaultIndex(parsedHeaders, field.headers);
			nextIndexes[field.key] = found >= 0 ? String(found) : "";
		});
		return {
			idIndex: idIndex >= 0 ? String(idIndex) : "",
			indexes: nextIndexes,
		};
	};

	const handleFileChange = async (event) => {
		const selectedFile = event.target.files?.[0];
		event.target.value = "";
		if (!selectedFile) return;

		setLoading(true);
		setError("");
		try {
			const isCsv = selectedFile.name.toLowerCase().endsWith(".csv");
			const parsedData = isCsv ? await parseCsvFile(selectedFile) : await parseExcelFile(selectedFile);
			const { idIndex, indexes } = detectColumnsForKeyType(parsedData.headers, idKeyType);

			setFileName(selectedFile.name);
			setHeaders(parsedData.headers);
			setSourceRows(parsedData.rows);
			setIdColumnIndex(idIndex);
			setFieldIndexes(indexes);
			setRowFeedback({});

			if (idIndex !== "" && OPTIONAL_FIELDS.some((field) => indexes[field.key] !== "")) {
				setPreviewRows(buildMappedRows(parsedData.rows, idIndex, indexes, idKeyType));
			} else {
				setPreviewRows([]);
			}
		} catch (uploadError) {
			resetState();
			setError(uploadError.message || "Unable to read the selected file.");
		} finally {
			setLoading(false);
		}
	};

	const handleIdKeyTypeChange = (nextKeyType) => {
		setIdKeyType(nextKeyType);
		setRowFeedback({});
		setError("");
		if (!headers.length) {
			setIdColumnIndex("");
			setPreviewRows([]);
			return;
		}
		const { idIndex, indexes } = detectColumnsForKeyType(headers, nextKeyType);
		setIdColumnIndex(idIndex);
		setFieldIndexes((prev) => {
			const merged = { ...prev };
			OPTIONAL_FIELDS.forEach((field) => {
				if (merged[field.key] === "" && indexes[field.key] !== "") merged[field.key] = indexes[field.key];
			});
			return merged;
		});
	};

	const handlePreview = async () => {
		setLoading(true);
		setError("");
		try {
			setPreviewRows(buildMappedRows(sourceRows));
			setRowFeedback({});
		} catch (previewError) {
			setError(previewError.message || "Unable to preview file.");
			setPreviewRows([]);
		} finally {
			setLoading(false);
		}
	};

	const handleDownloadFormat = async () => {
		const workbook = new ExcelJS.Workbook();
		const worksheet = workbook.addWorksheet("Sourcing Upload");
		const idPrefix = idKeyType === "style_ref_no" ? "REF" : "OPT";
		worksheet.addRow([
			idKeyLabel,
			"cogs",
			"cogs_remark",
			"wash_care",
			"hsn_code",
			"fabric_code",
			"fw_material_code",
		]);

		if (washCareOptions.length) {
			washCareOptions.forEach((washCareValue, index) => {
				const sampleId = `${idPrefix}_${String(index + 1).padStart(3, "0")}`;
				worksheet.addRow([
					sampleId,
					index === 0 ? 450 : "",
					index === 0 ? "sample remark" : "",
					washCareValue,
					index === 0 ? "61091000" : "",
					index === 0 ? "FAB123" : "",
					"",
				]);
			});
		} else {
			worksheet.addRow([`${idPrefix}_001`, 450, "sample remark", "", "61091000", "FAB123", ""]);
		}

		const buffer = await workbook.xlsx.writeBuffer();
		const blob = new Blob([buffer], {
			type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
		});
		const link = document.createElement("a");
		link.href = URL.createObjectURL(blob);
		link.download = `sourcing_bulk_upload_format_${idKeyLabel}.xlsx`;
		link.click();
		URL.revokeObjectURL(link.href);
	};

	const handleFormSubmit = async (event) => {
		event.preventDefault();
		if (typeof callbackFun !== "function") return;

		if (displayPreviewRows.some((row) => row.__isValid === false && row.__status === "Duplicate")) {
			setError(`Each ${idKeyLabel} must have the same values across all occurrences.`);
			return;
		}

		const invalidHsnPreviewRows = displayPreviewRows.filter(
			(row) => row.__isValid === false && String(row.__note || "").includes("HSN Code"),
		);
		if (invalidHsnPreviewRows.length) {
			setError("HSN Code must be a number and exactly 8 digits. Fix invalid rows before upload.");
			return;
		}

		if (displayPreviewRows.some((row) => row.__isValid === false)) {
			setError("Fix invalid rows before upload.");
			return;
		}

		setLoading(true);
		try {
			const uniqueRows = displayPreviewRows
				.filter((row) => row.__isValid !== false)
				.map((row) => {
					const payload = { [idKeyType]: row[idKeyType] };
					OPTIONAL_FIELDS.forEach((field) => {
						if (row[field.key] !== FIELD_UNSET) payload[field.key] = row[field.key];
					});
					return payload;
				});

			const uploadResult = await callbackFun(uniqueRows, { idType: idKeyType });
			const colorLevelConfig = COLOR_LEVEL_ID_TYPES[idKeyType];
			const missingIds = uploadResult?.result?.[colorLevelConfig.missingDetailKey] || [];
			const rejectedIds = uploadResult?.result?.[colorLevelConfig.rejectedDetailKey] || [];
			const blockedIds = uploadResult?.result?.[colorLevelConfig.blockedDetailKey] || [];
			const wrongDivisionIds = uploadResult?.result?.wrong_division_identifiers || [];
			const materialExemptIds = uploadResult?.result?.material_exempt_identifiers || [];
			const missingFabricCodes = uploadResult?.result?.missing_fabric_codes || [];
			const missingFwCodes = uploadResult?.result?.missing_fw_material_codes || [];
			const invalidWashCareValues = uploadResult?.result?.invalid_wash_care_values || [];
			const allowedWashCareValues = uploadResult?.result?.allowed_wash_care_values || [];
			const invalidHsnCodes = uploadResult?.result?.invalid_hsn_codes || [];
			const invalidHsnIdentifiers = uploadResult?.result?.invalid_hsn_identifiers || [];
			const conflictingStyles = uploadResult?.result?.conflicting_styles || [];

			const hasFeedback =
				missingIds.length > 0 ||
				rejectedIds.length > 0 ||
				blockedIds.length > 0 ||
				wrongDivisionIds.length > 0 ||
				materialExemptIds.length > 0 ||
				missingFabricCodes.length > 0 ||
				missingFwCodes.length > 0 ||
				invalidWashCareValues.length > 0 ||
				invalidHsnCodes.length > 0 ||
				invalidHsnIdentifiers.length > 0 ||
				conflictingStyles.length > 0;

			if (hasFeedback) {
				const nextFeedback = {};
				const missingSet = new Set(missingIds.map(normalizeKeyValue));
				const rejectedSet = new Set(rejectedIds.map(normalizeKeyValue));
				const blockedSet = new Set(blockedIds.map(normalizeKeyValue));
				const wrongDivisionSet = new Set(wrongDivisionIds.map(normalizeKeyValue));
				const materialExemptSet = new Set(materialExemptIds.map(normalizeKeyValue));
				const conflictMap = {};
				conflictingStyles.forEach((conflict) => {
					(conflict.option_ids || conflict.style_ref_nos || []).forEach((identifier) => {
						conflictMap[normalizeKeyValue(identifier)] = conflict;
					});
				});

				const invalidWashCareSet = new Set(invalidWashCareValues.map(normalizeKeyValue));
				const invalidHsnIdSet = new Set(invalidHsnIdentifiers.map(normalizeKeyValue));

				previewRows.forEach((row) => {
					const identifier = normalizeKeyValue(row[idKeyLabel]);
					if (missingSet.has(identifier)) {
						nextFeedback[identifier] = {
							__isValid: false,
							__status: "Missing",
							__note: `${identifier}: ${colorLevelConfig.notFoundLabel} not found`,
						};
					}
					if (rejectedSet.has(identifier)) {
						nextFeedback[identifier] = {
							__isValid: false,
							__status: "Rejected",
							__note: `${identifier}: Rejected color/style cannot be updated`,
						};
					}
					if (blockedSet.has(identifier)) {
						nextFeedback[identifier] = {
							__isValid: false,
							__status: "Blocked",
							__note: `${identifier}: MRP_PENDING / PUBLISHED styles cannot be bulk updated`,
						};
					}
					if (wrongDivisionSet.has(identifier)) {
						nextFeedback[identifier] = {
							__isValid: false,
							__status: "Division",
							__note: `${identifier}: fabric_code only for non-FOOT WEAR; fw_material_code only for FOOT WEAR`,
						};
					}
					if (materialExemptSet.has(identifier)) {
						nextFeedback[identifier] = {
							__isValid: false,
							__status: "Exempt",
							__note: `${identifier}: fabric_code / fw_material_code not allowed for PERSONAL CARE or ADDONS bag styles`,
						};
					}
					if (conflictMap[identifier]) {
						nextFeedback[identifier] = {
							__isValid: false,
							__status: "Conflict",
							__note: `Style ${conflictMap[identifier].style_id} has conflicting style-level values across colors`,
						};
					}
					const washCareValue = normalizeKeyValue(displayValue(row.wash_care));
					if (washCareValue && invalidWashCareSet.has(washCareValue)) {
						nextFeedback[identifier] = {
							__isValid: false,
							__status: "Invalid",
							__note: `${identifier}: wash_care '${washCareValue}' is not in dropdown`,
						};
					}
					if (invalidHsnIdSet.has(identifier)) {
						nextFeedback[identifier] = {
							__isValid: false,
							__status: "Invalid",
							__note: `${identifier}: HSN Code must be exactly 8 digits`,
						};
					}
				});

				if (
					missingFabricCodes.length ||
					missingFwCodes.length ||
					invalidWashCareValues.length ||
					invalidHsnCodes.length
				) {
					setError(
						[
							uploadResult?.message,
							missingFabricCodes.length ? `Missing fabric codes: ${missingFabricCodes.join(", ")}` : "",
							missingFwCodes.length ? `Missing fw material codes: ${missingFwCodes.join(", ")}` : "",
							invalidWashCareValues.length
								? `Invalid wash_care: ${invalidWashCareValues.join(", ")}${
										allowedWashCareValues.length
											? ` (allowed: ${allowedWashCareValues.join(", ")})`
											: ""
									}`
								: "",
							invalidHsnCodes.length
								? `Invalid hsn_code: ${invalidHsnCodes.join(", ")} (must be exactly 8 digits)`
								: "",
						]
							.filter(Boolean)
							.join(" | "),
					);
				} else {
					setError(uploadResult?.message || "Upload validation failed.");
				}
				setRowFeedback(nextFeedback);
				return;
			}

			if (uploadResult?.success === false) {
				setError(uploadResult?.message || "Upload failed.");
				return;
			}

			if (uploadResult !== false) handleClose();
		} finally {
			setLoading(false);
		}
	};

	const handleDeleteRow = (rowId) => {
		setPreviewRows((prev) => {
			const nextRows = prev.filter((row) => row.__rowId !== rowId);
			const removedRow = prev.find((row) => row.__rowId === rowId);
			const removedKey = normalizeKeyValue(removedRow?.[idKeyLabel]);
			if (removedKey) {
				setRowFeedback((current) => {
					const nextFeedback = { ...current };
					delete nextFeedback[removedKey];
					return nextFeedback;
				});
			}
			return nextRows;
		});
	};

	const displayPreviewRows = useMemo(
		() =>
			previewRows.map((row) => {
				const feedback = rowFeedback[normalizeKeyValue(row[idKeyLabel])];
				return feedback ? { ...row, ...feedback } : row;
			}),
		[previewRows, rowFeedback, idKeyLabel],
	);

	const displayValue = (value) => (value === FIELD_UNSET ? "" : value);

	const uploadPreviewColumns = useMemo(
		() => [
			{
				key: "__upload_status",
				name: "UPLOAD STATUS",
				headerAlign: "center",
				align: "center",
				minWidth: 120,
				maxWidth: 140,
				valueGetter: ({ row }) =>
					row.__isValid === false ? row.__status || "Invalid" : row.__status || "Valid",
				renderCell: ({ row }) => (
					<Box color={row.__isValid === false ? "error.main" : "success.main"} fontWeight={600}>
						{row.__isValid === false ? row.__status || "Invalid" : row.__status || "Valid"}
					</Box>
				),
			},
			{
				key: idKeyLabel,
				name: idKeyLabel.toUpperCase(),
				headerAlign: "center",
				align: "center",
				minWidth: 140,
			},
			...OPTIONAL_FIELDS.map((field) => ({
				key: field.key,
				name: field.label.toUpperCase(),
				headerAlign: "center",
				align: "center",
				minWidth: 120,
				valueGetter: ({ row }) => displayValue(row[field.key]),
				renderCell: ({ row }) => (
					<Typography variant="body2" textAlign="center" sx={{ width: "100%" }}>
						{displayValue(row[field.key])}
					</Typography>
				),
			})),
			{
				key: "__note",
				name: "NOTE",
				headerAlign: "center",
				align: "center",
				minWidth: 240,
				valueGetter: ({ row }) => row.__note || "",
				renderCell: ({ row }) => (
					<Typography
						variant="body2"
						color={row.__isValid === false ? "error.main" : "text.secondary"}
						sx={{ width: "100%" }}
						textAlign="center">
						{row.__note || ""}
					</Typography>
				),
			},
			{
				key: "__delete",
				name: "DELETE",
				headerAlign: "center",
				align: "center",
				minWidth: 90,
				maxWidth: 90,
				valueGetter: () => "",
				renderCell: ({ row }) => (
					<IconButton
						size="small"
						color="error"
						onClick={(event) => {
							event.stopPropagation();
							handleDeleteRow(row.__rowId);
						}}>
						<DeleteOutlineRounded fontSize="small" />
					</IconButton>
				),
			},
		],
		[idKeyLabel],
	);

	const validCount = [
		...new Set(displayPreviewRows.filter((row) => row.__isValid !== false).map((row) => row[idKeyLabel])),
	].length;

	const previewGridKey = useMemo(
		() => displayPreviewRows.map((row) => `${row.__rowId}:${row.__status}:${row.__note}:${row.__isValid}`).join("||"),
		[displayPreviewRows],
	);

	const canPreview = !loading && headers.length > 0 && idColumnIndex !== "" && hasAnyFieldMapped();

	return (
		<Dialog maxWidth="xl" fullWidth scroll="paper" open={modal} onClose={handleClose} PaperProps={{ sx: { height: "82vh" } }}>
			<DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
				<Typography variant="h6">Bulk Sourcing Upload</Typography>
				<IconButton onClick={handleClose}>
					<ClearRounded />
				</IconButton>
			</DialogTitle>
			<DialogContent sx={{ display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden", pt: 1.5, pb: 1.5 }}>
				<Stack spacing={1.25} sx={{ minHeight: 0, flex: 1, overflow: "hidden" }}>
					{previewRows.length === 0 && (
						<Alert severity="info" sx={{ py: 0.5 }}>
							Upload by <strong>option_id</strong> / <strong>style_ref_no</strong>. Color fields:{" "}
							<strong>cogs</strong>, <strong>cogs_remark</strong>. Style fields: <strong>wash_care</strong>,{" "}
							<strong>hsn_code</strong>, <strong>fabric_code</strong>, <strong>fw_material_code</strong>. Empty =
							unchanged. <strong>MRP_PENDING / PUBLISHED</strong> styles are blocked.
						</Alert>
					)}

					<Box>
						<Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
							<FormControl size="small" sx={{ minWidth: 180 }}>
								<InputLabel id="upload-sourcing-id-type-label">Upload By</InputLabel>
								<Select
									labelId="upload-sourcing-id-type-label"
									label="Upload By"
									value={idKeyType}
									onChange={(event) => handleIdKeyTypeChange(event.target.value)}
									disabled={loading}>
									<MenuItem value={ID_KEY_OPTIONS.option_id.key}>option_id</MenuItem>
									<MenuItem value={ID_KEY_OPTIONS.style_ref_no.key}>style_ref_no</MenuItem>
								</Select>
							</FormControl>
							<Button
								component="label"
								variant="contained"
								size="small"
								disableElevation
								sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
								startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <UploadRounded />}>
								Choose CSV / Excel
								<input hidden type="file" accept=".csv,.xlsx" onChange={handleFileChange} disabled={loading} />
							</Button>
							<Button
								variant="outlined"
								color="secondary"
								size="small"
								onClick={handleDownloadFormat}
								disabled={loading}
								startIcon={<FileDownloadRounded />}
								sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2, borderStyle: "dashed" }}>
								Upload Format
							</Button>
							<Button
								variant="contained"
								color="success"
								size="small"
								disableElevation
								onClick={handlePreview}
								disabled={!canPreview}
								startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshRounded />}
								sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}>
								Preview Rows
							</Button>
						</Stack>
						{fileName && (
							<Typography variant="body2" color="text.secondary" pt={1}>
								{`${fileName} - ${sourceRows.length} rows found`}
							</Typography>
						)}
					</Box>

					{headers.length > 0 && (
						<Box display="flex" flexWrap="wrap" gap={1.5} pt={0.5}>
							<FormControl size="small" sx={{ minWidth: 180 }}>
								<InputLabel>{idKeyLabel} Column</InputLabel>
								<Select
									label={`${idKeyLabel} Column`}
									value={idColumnIndex}
									onChange={(event) => setIdColumnIndex(event.target.value)}>
									{headers.map((header, index) => (
										<MenuItem key={`${header}-id-${index}`} value={String(index)}>
											{header}
										</MenuItem>
									))}
								</Select>
							</FormControl>
							{OPTIONAL_FIELDS.map((field) => (
								<FormControl key={field.key} size="small" sx={{ minWidth: 160 }}>
									<InputLabel>{field.label} Column</InputLabel>
									<Select
										label={`${field.label} Column`}
										value={fieldIndexes[field.key]}
										onChange={(event) =>
											setFieldIndexes((prev) => ({ ...prev, [field.key]: event.target.value }))
										}>
										<MenuItem value="">
											<em>None</em>
										</MenuItem>
										{headers.map((header, index) => (
											<MenuItem key={`${header}-${field.key}-${index}`} value={String(index)}>
												{header}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							))}
						</Box>
					)}

					{error && <Alert severity="error">{error}</Alert>}

					<Stack sx={{ minHeight: 0, flex: 1, overflow: "hidden" }}>
						{previewRows.length > 0 && (
							<Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
								<TanstackDatagrid
									key={`${previewGridKey}-${idKeyLabel}`}
									columns={uploadPreviewColumns}
									pageData={displayPreviewRows}
									setPageData={setPreviewRows}
									getRowId={(row) => row.__rowId}
									disablePagination={true}
									serverSidePagination={false}
									enableHorizontalScroll={true}
									headerHeight={40}
									rowHeight={35}
									processRowUpdate={(newRow) => newRow}
									isCellEditable={({ column }) =>
										!["__upload_status", "__note", "__delete"].includes(column.key)
									}
									children={
										<Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
											<Typography variant="body2" color="text.secondary">
												{`${validCount} unique ${idKeyLabel}${validCount === 1 ? "" : "s"} can be uploaded from ${sourceRows.length} rows`}
											</Typography>
										</Box>
									}
								/>
							</Box>
						)}
					</Stack>
				</Stack>
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2 }}>
				<Button variant="outlined" onClick={handleClose} disabled={loading}>
					Cancel
				</Button>
				<Button variant="contained" onClick={handleFormSubmit} disabled={loading || validCount === 0}>
					Save Upload
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default UploadModal;
